import { useState, useCallback } from 'react';
import type { DataSource } from '@veodiagram/shared';

interface SourceFilesDisplayProps {
  dataSources: DataSource[];
  diagramId: string;
  maxVisible?: number;
}

/**
 * Extract display name from path or use description fallback (Issue #175)
 */
function getDisplayName(source: DataSource): string {
  if (source.path) {
    const parts = source.path.split('/');
    return parts[parts.length - 1] || source.path;
  }
  // Use description as fallback for AI analysis sources without paths
  if (source.description) {
    // Truncate long descriptions
    return source.description.length > 30
      ? source.description.substring(0, 27) + '...'
      : source.description;
  }
  return 'Unknown';
}

/**
 * Format tooltip content for a data source
 */
function formatTooltipContent(source: DataSource): string {
  const lines: string[] = [];

  if (source.path) {
    lines.push(`Path: ${source.path}`);
  }

  if (source.functions && source.functions.length > 0) {
    lines.push(`Functions: ${source.functions.slice(0, 5).join(', ')}${source.functions.length > 5 ? '...' : ''}`);
  }

  if (source.lineNumbers) {
    lines.push(`Lines: ${source.lineNumbers}`);
  }

  if (source.patterns && source.patterns.length > 0) {
    lines.push(`Patterns: ${source.patterns.join(', ')}`);
  }

  if (source.description) {
    lines.push(`Info: ${source.description}`);
  }

  return lines.join('\n');
}

/**
 * Get icon for source type
 */
function getSourceTypeIcon(type: DataSource['type']): string {
  switch (type) {
    case 'file': return '📄';
    case 'pattern': return '🔍';
    case 'api': return '🌐';
    case 'config': return '⚙️';
    case 'ast': return '🌳';
    default: return '📄';
  }
}

/**
 * SourceFilesDisplay - Shows source files with hover tooltips and click-to-detail
 */
export function SourceFilesDisplay({ dataSources, diagramId: _diagramId, maxVisible = 3 }: SourceFilesDisplayProps) {
  const [expandedSource, setExpandedSource] = useState<DataSource | null>(null);
  const [showAllSources, setShowAllSources] = useState(false);

  const handleSourceClick = useCallback((source: DataSource, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSource(expandedSource === source ? null : source);
  }, [expandedSource]);

  const handleCloseDetail = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSource(null);
  }, []);

  // Issue #184: Handle click on "+X more" badge to show all sources
  const handleShowMoreClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowAllSources(prev => !prev);
  }, []);

  if (!dataSources || dataSources.length === 0) {
    return null;
  }

  // Issue #184: Show all sources when expanded
  const visibleSources = showAllSources ? dataSources : dataSources.slice(0, maxVisible);
  const remainingCount = dataSources.length - maxVisible;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1 text-muted-foreground text-xs">
        <span>Sources:</span>
      </div>

      {/* Source file chips */}
      <div className="flex flex-wrap gap-1">
        {visibleSources.map((source, index) => (
          <button
            key={index}
            onClick={(e) => handleSourceClick(source, e)}
            title={formatTooltipContent(source)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted hover:bg-muted/80 rounded text-xs transition-colors cursor-pointer max-w-[150px]"
          >
            <span>{getSourceTypeIcon(source.type)}</span>
            <span className="truncate">{getDisplayName(source)}</span>
            {(source.functions?.length || source.lineNumbers) && (
              <span className="text-muted-foreground">*</span>
            )}
          </button>
        ))}

        {/* Issue #184: Make "+X more" clickable to expand/collapse */}
        {remainingCount > 0 && (
          <button
            onClick={handleShowMoreClick}
            className="inline-flex items-center px-1.5 py-0.5 text-xs text-primary hover:text-primary/80 hover:underline cursor-pointer"
          >
            {showAllSources ? 'Show less' : `+${remainingCount} more`}
          </button>
        )}
      </div>

      {/* Expanded detail view */}
      {expandedSource && (
        <div
          className="mt-2 p-3 bg-background border border-border rounded-lg shadow-lg text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 font-medium">
              <span>{getSourceTypeIcon(expandedSource.type)}</span>
              <span>{getDisplayName(expandedSource)}</span>
            </div>
            <button
              onClick={handleCloseDetail}
              className="text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>

          <div className="space-y-2 text-muted-foreground">
            {expandedSource.path && (
              <div>
                <span className="font-medium text-foreground">Full Path:</span>
                <div className="mt-0.5 font-mono text-[10px] bg-muted p-1 rounded break-all">
                  {expandedSource.path}
                </div>
              </div>
            )}

            {expandedSource.description && (
              <div>
                <span className="font-medium text-foreground">Description:</span>
                <div className="mt-0.5">{expandedSource.description}</div>
              </div>
            )}

            {expandedSource.functions && expandedSource.functions.length > 0 && (
              <div>
                <span className="font-medium text-foreground">Functions ({expandedSource.functions.length}):</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {expandedSource.functions.map((fn, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-mono text-[10px]">
                      {fn}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {expandedSource.lineNumbers && (
              <div>
                <span className="font-medium text-foreground">Lines:</span>
                <span className="ml-1 font-mono">{expandedSource.lineNumbers}</span>
              </div>
            )}

            {expandedSource.patterns && expandedSource.patterns.length > 0 && (
              <div>
                <span className="font-medium text-foreground">Patterns Found:</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {expandedSource.patterns.map((pattern, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-[10px]">
                      {pattern}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {expandedSource.type && (
              <div className="pt-1 border-t border-border">
                <span className="text-[10px]">Type: {expandedSource.type}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
